import { MenuDivider, MenuItem, MenuList } from "@fluentui/react-components";
import React, { ReactElement, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppRouteParams } from "@/AppRoutes";
import { useVersionCategoryDeleteDialog } from "@/components/Dialog/DialogServer/DialogVersionCategories/useVersionCategoryDeleteDialog";
import { DialogPhaseType } from "@/components/Dialog/helpers/hooks";
import { useCachedOrganization } from "@/components/InOrganizationProvider/OrganizationProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { VersionCategoryRaw } from "@/domain/VersionCategories";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";

const useTranslation = translation.use(nsVersion);

/**
 * @description カテゴリーの3点メニュー項目を生成するフック
 */
export const useContextMenuItemsCategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId } = useAppRouteParams();
  // シート機能の有効/無効をリアルタイムに反映するため、
  // マウント時に一度だけ取得する useAppOrgsAndInvitedOrgs ではなく
  // Firestore リスナーで最新化される useCachedOrganization を参照する
  const { organization } = useCachedOrganization();
  const { hasPermission, hasPermissionWithId } = usePermissions();
  const { versionCategories, runReloadVersionCategories } =
    useVersionCategories();
  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const [targetCategoryId, setTargetCategoryId] = useState<string | null>(null);

  const clearTargetCategoryId = useCallback(() => {
    setTargetCategoryId(null);
  }, []);

  const { renderVersionCategoryDeleteDialog } = useVersionCategoryDeleteDialog({
    targetCategoryId,
    versionCategories,
    runReloadVersionCategories,
    isAdminMode: false,
    onDeleted: clearTargetCategoryId,
    dialogPhase,
    setDialogPhase,
  });

  const handleDeleteCategory = useCallback(async (categoryId: string) => {
    setTargetCategoryId(categoryId);
    setDialogPhase("confirm");
  }, []);

  const getContextMenuItems = useCallback(
    (category: VersionCategoryRaw | null): ReactElement | null => {
      const hasVersionsMetaWritePermission = hasPermission(
        "versions_meta",
        "write"
      );
      const hasVersionCategoriesWritePermission = hasPermission(
        "version_categories",
        "write"
      );
      const hasVersionCategoriesDeletePermission = hasPermission(
        "version_categories",
        "delete"
      );

      // カテゴリーへの書き込み権限をチェック
      const canWriteToCategory =
        hasPermission("versions", "write") ||
        (category?.id &&
          hasPermissionWithId(
            "versions_version_category",
            "write",
            category.id
          ));

      // バージョン作成には versions_meta:write と カテゴリー書き込み権限が必要
      // シート機能が無効な組織では作成不可
      const canCreateVersion =
        hasVersionsMetaWritePermission &&
        canWriteToCategory &&
        organization?.is_sheet_enabled === true;
      // スキーマアップロードには global versions:write が必要
      // シート機能が無効な組織では作成不可
      const canCreateVersionFromUpload =
        hasVersionsMetaWritePermission &&
        hasPermission("versions", "write") &&
        organization?.is_sheet_enabled === true;
      // カテゴリー編集には global またはスコープ付き権限が必要
      const canEditCategory =
        hasVersionCategoriesWritePermission ||
        (category?.id &&
          hasPermissionWithId("version_category", "write", category.id));
      // カテゴリー並び替えには global 権限が必要（複数カテゴリーを更新するため）
      const canSortCategories = hasVersionCategoriesWritePermission;
      // カテゴリー削除には version_categories:delete が必要
      const canDeleteCategory =
        hasVersionCategoriesDeletePermission ||
        (category?.id &&
          hasPermissionWithId("version_category", "delete", category.id));

      // 未分類カテゴリーではメニュー（3点リーダー）自体を表示しない
      const isUncategorized = !category?.id;
      if (isUncategorized) {
        return null;
      }

      // 使える機能のメニュー項目のみを表示する（使えない項目は非表示）
      const createItems: ReactElement[] = [];
      if (canCreateVersion) {
        createItems.push(
          <MenuItem
            key="create-version"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/organizations/${organizationId}/versions/create`, {
                state: { preSelectedCategoryId: category?.id || "" },
              });
            }}
          >
            {t.version.create.newVersion()}
          </MenuItem>
        );
      }
      if (canCreateVersionFromUpload) {
        createItems.push(
          <MenuItem
            key="create-version-from-upload"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/organizations/${organizationId}/versions/upload_sheet_schema`,
                {
                  state: { preSelectedCategoryId: category?.id || "" },
                }
              );
            }}
          >
            {t.version.create.newVersionFromConfigFile()}
          </MenuItem>
        );
      }

      const categoryItems: ReactElement[] = [];
      if (canEditCategory) {
        categoryItems.push(
          <MenuItem
            key="edit-category"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/organizations/${organizationId}/organization_setting/version_categories/${category.id}/edit`
              );
            }}
          >
            {t.version.category.rename()}
          </MenuItem>
        );
      }
      if (canSortCategories) {
        categoryItems.push(
          <MenuItem
            key="sort-categories"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/organizations/${organizationId}/organization_setting/version_categories/sort`
              );
            }}
          >
            {t.version.category.sort()}
          </MenuItem>
        );
      }
      if (canDeleteCategory) {
        categoryItems.push(
          <MenuItem
            key="delete-category"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCategory(category.id);
            }}
            style={{ color: "var(--colorPaletteRedForeground1)" }}
          >
            {t.version.category.delete()}
          </MenuItem>
        );
      }

      // 使える機能が一つもない場合はメニュー（3点リーダー）自体を表示しない
      if (createItems.length === 0 && categoryItems.length === 0) {
        return null;
      }

      return (
        <MenuList>
          {createItems}
          {createItems.length > 0 && categoryItems.length > 0 && (
            <MenuDivider />
          )}
          {categoryItems}
        </MenuList>
      );
    },
    [
      t,
      hasPermission,
      hasPermissionWithId,
      navigate,
      organizationId,
      organization,
      handleDeleteCategory,
    ]
  );

  return {
    getContextMenuItems,
    renderDeleteDialog: renderVersionCategoryDeleteDialog,
  };
};
